import { useState } from "react";
import {
  TrendingUp,
  TrendingDown,
  Clock,
  Copy,
  Check,
  X,
  Info,
  Shield,
} from "lucide-react";
import { C, labelStyle } from "../../theme/tokens";
import { formatPairLabelSafe } from "../../data/pairs";
import { useCountdown, formatCountdown } from "../../hooks/useActiveSignal";
import { timeframeToSeconds } from "../../utils/time";

/* Full-screen binary signal with direction, expiry, entry, recommendation,
  market comment, and outcome controls. */
export function ActiveSignalOverlay({
  signal,
  requestedPair,
  titleLabel,
  startedAt,
  t,
  onSubmitResult,
  onClose,
  onCancel,
  submitting,
  resultError,
}) {
  const [copiedKey, setCopiedKey] = useState(null);

  const suppliedDuration = Number(signal?.time_seconds);
  const durationSec = Number.isFinite(suppliedDuration) && suppliedDuration > 0
    ? suppliedDuration
    : signal?.expiration
      ? timeframeToSeconds(signal.expiration)
      : signal?.timeframe
        ? timeframeToSeconds(signal.timeframe)
        : null;

  const deadlineMs = startedAt && durationSec ? startedAt + durationSec * 1000 : null;
  const remaining = useCountdown(deadlineMs);
  const expired = deadlineMs ? remaining <= 0 : true;

  const isUp = signal?.direction === "UP";
  const DirIcon = isUp ? TrendingUp : TrendingDown;
  const dirColor = isUp ? C.greenBright : "#C4604A";
  const dirGradient = isUp
    ? `linear-gradient(135deg, ${C.green}33 0%, ${C.greenBright}18 100%)`
    : `linear-gradient(135deg, #C4604A33 0%, #C4604A18 100%)`;

  const heading = titleLabel || formatPairLabelSafe(signal?.asset || requestedPair);
  const isOtc = (signal?.asset || requestedPair || "").endsWith("_otc");

  const entryPrice = signal?.entry_price ?? signal?.price ?? "—";
  const expiration = signal?.expiration || signal?.timeframe || "";
  const entrySection = signal?.entry_section;
  const comment = signal?.comment;

  const handleCopy = (key, value) => {
    if (!value || value === "—") return;
    try {
      navigator.clipboard?.writeText(String(value));
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1800);
    } catch (e) {}
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(10, 12, 16, 0.88)",
        backdropFilter: "blur(8px)",
        overflowY: "auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `radial-gradient(circle at 15% 5%, ${dirColor}25, transparent 50%), radial-gradient(circle at 85% 95%, ${C.violet}20, transparent 55%)`,
          pointerEvents: "none",
        }}
      />

      <div style={{ position: "relative", width: "100%", maxWidth: 460, margin: "auto" }}>
        <div
          style={{
            borderRadius: 22,
            background: `linear-gradient(165deg, ${C.panel1} 0%, ${C.panel2} 100%)`,
            border: `1px solid ${C.hairline}`,
            padding: "20px 20px 24px",
            boxShadow: `0 20px 50px rgba(0, 0, 0, 0.5), 0 0 30px ${dirColor}15`,
            position: "relative",
          }}
        >
          {/* Top Bar: Asset + OTC badge + Close X */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 14,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <span
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: 20,
                  color: C.primary,
                  letterSpacing: "-0.01em",
                }}
              >
                {heading.replace(" OTC", "")}
              </span>
              {isOtc && (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: C.violetBright,
                    background: `${C.violetBright}26`,
                    border: `1px solid ${C.violetBright}4D`,
                    padding: "2px 7px",
                    borderRadius: 6,
                    letterSpacing: "0.04em",
                  }}
                >
                  OTC
                </span>
              )}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {expiration && (
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "4px 10px",
                    borderRadius: 10,
                    background: C.panel2,
                    border: `1px solid ${C.hairline}`,
                    fontSize: 12,
                    fontFamily: "'JetBrains Mono', monospace",
                    fontWeight: 600,
                    color: C.secondary,
                  }}
                >
                  <Clock size={12} color={C.mutedB} />
                  {expiration}
                </div>
              )}

              {onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  aria-label={t.sig_close_modal || "Close"}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 10,
                    border: `1px solid ${C.hairline}`,
                    background: C.panel2,
                    color: C.secondary,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Direction Banner + Signal Meta */}
          <div
            style={{
              borderRadius: 16,
              background: dirGradient,
              border: `1px solid ${dirColor}66`,
              padding: "16px",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 18px",
                borderRadius: 20,
                background: `${dirColor}26`,
                border: `1px solid ${dirColor}88`,
                boxShadow: `0 0 16px ${dirColor}33`,
              }}
            >
              <DirIcon size={22} color={dirColor} strokeWidth={2.5} />
              <span
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 800,
                  fontSize: 19,
                  color: dirColor,
                  letterSpacing: "0.02em",
                }}
              >
                {isUp ? t.sig_direction_up : t.sig_direction_down}
              </span>
            </div>

            {deadlineMs && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 11, color: C.mutedB, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {expired ? t.sig_choose_result : t.sig_wait_sub}
                </div>
                <div
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontWeight: 700,
                    fontSize: 26,
                    color: expired ? C.greenLight : C.primary,
                    marginTop: 2,
                  }}
                >
                  {formatCountdown(remaining)}
                </div>
              </div>
            )}
          </div>

          {/* Binary signal entry price */}
          <div style={{ marginTop: 14 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: C.mutedB,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginBottom: 8,
              }}
            >
              {t.sig_field_price || "Ціна входу"}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
              {/* Entry Price */}
              <div
                onClick={() => handleCopy("entry", entryPrice)}
                style={{
                  background: C.panel1,
                  border: `1px solid ${copiedKey === "entry" ? C.greenBright : C.hairline}`,
                  borderRadius: 14,
                  padding: "12px 14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  transition: "border-color .2s ease",
                }}
              >
                <div>
                  <div style={labelStyle}>{t.sig_field_entry_price || t.sig_field_price}</div>
                  <div
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontWeight: 700,
                      fontSize: 20,
                      color: C.primary,
                      marginTop: 2,
                    }}
                  >
                    {entryPrice}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 5, color: copiedKey === "entry" ? C.greenBright : C.mutedB, fontSize: 11, fontWeight: 600 }}>
                  {copiedKey === "entry" ? (
                    <>
                      <Check size={14} color={C.greenBright} />
                      <span>{t.sig_copied || "Скопійовано"}</span>
                    </>
                  ) : (
                    <Copy size={15} />
                  )}
                </div>
              </div>

            </div>
          </div>

          {/* Entry Condition / Recommendation */}
          {entrySection && (
            <div
              style={{
                marginTop: 12,
                background: C.panel1,
                border: `1px solid ${C.hairline}`,
                borderRadius: 14,
                padding: "12px 14px",
              }}
            >
              <div style={{ ...labelStyle, display: "flex", alignItems: "center", gap: 5 }}>
                <Info size={12} color={C.violetBright} />
                {t.sig_field_entry || "Умова входу"}
              </div>
              <div style={{ fontSize: 13, color: C.primary, lineHeight: 1.5, marginTop: 4 }}>
                {entrySection}
              </div>
            </div>
          )}

          {/* Market Analysis / Comment */}
          {comment && (
            <div
              style={{
                marginTop: 10,
                background: C.panel1,
                border: `1px solid ${C.hairline}`,
                borderRadius: 14,
                padding: "12px 14px",
              }}
            >
              <div style={labelStyle}>{t.sig_field_comment || "Аналіз ринку"}</div>
              <div style={{ fontSize: 12.5, color: C.secondary, lineHeight: 1.5, marginTop: 4 }}>
                {comment}
              </div>
            </div>
          )}

          {/* Outcome Actions */}
          <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                onClick={() => onSubmitResult(true)}
                disabled={submitting}
                style={{
                  flex: 1,
                  padding: "14px 12px",
                  borderRadius: 14,
                  border: "none",
                  background: submitting
                    ? C.mutedA
                    : `linear-gradient(120deg, ${C.greenBright} 0%, ${C.green} 100%)`,
                  color: submitting ? C.mutedB : "#0b0d13",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: submitting ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  boxShadow: `0 4px 14px ${C.greenBright}33`,
                }}
              >
                <Check size={16} />
                {t.sig_btn_profit || "Угода в плюс"}
              </button>

              <button
                type="button"
                onClick={() => onSubmitResult(false)}
                disabled={submitting}
                style={{
                  flex: 1,
                  padding: "14px 12px",
                  borderRadius: 14,
                  border: `1px solid ${submitting ? C.hairline : "#EF4444"}`,
                  background: "rgba(239, 68, 68, 0.08)",
                  color: submitting ? C.mutedB : "#EF4444",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: submitting ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                }}
              >
                <Shield size={16} />
                {t.sig_btn_loss || "Угода в мінус"}
              </button>
            </div>

            {resultError && (
              <div style={{ color: "#C4604A", fontSize: 12.5, textAlign: "center", marginTop: 4 }}>
                {t.sig_result_error}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              {onCancel && (
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={submitting}
                  style={{
                    flex: 1,
                    padding: "11px 12px",
                    borderRadius: 12,
                    border: `1px solid ${C.hairline}`,
                    background: "transparent",
                    color: C.secondary,
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: submitting ? "not-allowed" : "pointer",
                  }}
                >
                  {t.sig_btn_no_trade || "Не входив у угоду"}
                </button>
              )}

              {onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    flex: 1,
                    padding: "11px 12px",
                    borderRadius: 12,
                    border: `1px solid ${C.hairline}`,
                    background: C.panel2,
                    color: C.primary,
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  {t.sig_close_modal || "Згорнути"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}